<script setup lang="ts">
import { onMounted, nextTick, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import BravoButton from '@services/ui-component-library/components/BravoButton.vue'
import SsoCompletingIndicator from '@/components/SsoCompletingIndicator.vue'
import { useSSO } from '@/composables/services/useSSO'

const route = useRoute()
const router = useRouter()
const { submitAcsLogin } = useSSO()

const errorMessage = ref<string | null>(null)

onMounted(async () => {
  const preflight = route.query.preflight as string
  const ptid = route.query.ptid as string

  if (!preflight?.trim() || !isValidUuid(preflight)) {
    console.warn('Invalid or missing preflight parameter, redirecting to login')
    await router.push('/login')
    return
  }
  if (!ptid?.trim()) {
    console.warn('Missing ptid parameter, redirecting to login')
    await router.push('/login')
    return
  }

  await nextTick()

  try {
    await submitAcsLogin(preflight.trim(), ptid.trim())
  } catch (err) {
    const fallback =
      'SSO could not be completed. The link may have expired (preflight expires after 60 seconds). Please try signing in again from the login page.'
    errorMessage.value = err instanceof Error && err.message.trim() ? err.message : fallback
  }
})

const isValidUuid = (uuid: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidRegex.test(uuid)
}

const goToLogin = () => {
  router.push('/login')
}
</script>

<template>
  <div class="acs-view" data-testid="acs-view">
    <div v-if="errorMessage" class="acs-error-container">
      <p class="acs-error-message">{{ errorMessage }}</p>
      <BravoButton
        label="Return to login"
        severity="primary"
        @click="goToLogin"
        data-testid="acs-error-login-link"
      />
    </div>
    <div v-else class="acs-loading-container">
      <SsoCompletingIndicator />
    </div>
  </div>
</template>

<style scoped>
.acs-view {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: var(--color-surface, #ffffff);
}

.acs-loading-container,
.acs-error-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem;
}

.acs-error-message {
  color: var(--color-text, #333333);
  text-align: center;
  max-width: 400px;
  margin: 0;
}
</style>
